/**
 * Detection Metrics — Sprint 16b Package 2
 *
 * Records detection run metrics and entity feedback to the Store.
 * Handles retention cleanup (90 days for metrics, 30 days for feedback).
 */
import { NS } from '@ownyou/shared-types';
/** Retention: 90 days for detection metrics */
const METRICS_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
/** Retention: 30 days for entity feedback */
const FEEDBACK_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * Record a detection run's metrics to the Store.
 */
export async function recordDetectionRun(userId, metrics, store) {
    const namespace = NS.ikigaiMetrics(userId);
    await store.put(namespace, metrics.runId, metrics);
}
/**
 * Record user feedback on a detected entity.
 */
export async function recordEntityFeedback(userId, feedback, store) {
    const namespace = NS.ikigaiFeedback(userId);
    const key = `${feedback.entityId}-${feedback.type}-${feedback.timestamp}`;
    await store.put(namespace, key, feedback);
}
/**
 * Get all detection metrics for a user.
 */
export async function getDetectionMetrics(userId, store) {
    try {
        const namespace = NS.ikigaiMetrics(userId);
        const result = await store.list(namespace);
        return result.items.filter(r => !r._deleted);
    }
    catch {
        return [];
    }
}
/**
 * Get all feedback for a user.
 */
export async function getEntityFeedbackList(userId, store) {
    try {
        const namespace = NS.ikigaiFeedback(userId);
        const result = await store.list(namespace);
        return result.items.filter(r => !r._deleted);
    }
    catch {
        return [];
    }
}
/**
 * Sweep stale metrics and feedback records.
 * - Metrics older than 90 days are deleted
 * - Feedback older than 30 days is deleted
 *
 * Should run at the start of each pipeline run.
 */
export async function sweepStaleMetrics(userId, store, now = Date.now()) {
    let metricsDeleted = 0;
    let feedbackDeleted = 0;
    // Sweep stale metrics
    try {
        const metricsNs = NS.ikigaiMetrics(userId);
        const result = await store.list(metricsNs);
        for (const m of result.items) {
            if (m._deleted)
                continue;
            if (m.timestamp && now - m.timestamp > METRICS_RETENTION_MS) {
                await store.put(metricsNs, m.runId, { _deleted: true });
                metricsDeleted++;
            }
        }
    }
    catch {
        // Ignore errors during sweep
    }
    // Sweep stale feedback
    try {
        const feedbackNs = NS.ikigaiFeedback(userId);
        const result = await store.list(feedbackNs);
        for (const f of result.items) {
            if (f._deleted)
                continue;
            if (f.timestamp && now - f.timestamp > FEEDBACK_RETENTION_MS) {
                const key = `${f.entityId}-${f.type}-${f.timestamp}`;
                await store.put(feedbackNs, key, { _deleted: true });
                feedbackDeleted++;
            }
        }
    }
    catch {
        // Ignore errors during sweep
    }
    return { metricsDeleted, feedbackDeleted };
}
//# sourceMappingURL=detection-metrics.js.map